import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/components/finance/financeUtils";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock3, CreditCard, Download, Loader2, ReceiptText, XCircle } from "lucide-react";
import { toast } from "sonner";

// Every figure on these cards comes from the server's finance records; the
// browser never computes or sends amounts.

export const REASON_TEXT = {
  no_current_term: "Ordering is paused until the current term is set.",
  not_eligible_for_class: "Not offered for this child's class.",
  choose_programme: "Choose the programme this is for.",
  unpaid_order_exists: "An earlier order is still unpaid.",
  term_limit_reached: "Term limit reached.",
};

const STATE = {
  confirmed: { label: "Confirmed", tone: "text-emerald-700 bg-emerald-50", Icon: CheckCircle2 },
  awaiting_payment: { label: "Awaiting payment", tone: "text-amber-700 bg-amber-50", Icon: Clock3 },
  processing: { label: "Processing", tone: "text-amber-700 bg-amber-50", Icon: Clock3 },
  initializing: { label: "Starting", tone: "text-amber-700 bg-amber-50", Icon: Clock3 },
  expired: { label: "Expired", tone: "text-slate-600 bg-slate-100", Icon: XCircle },
  failed: { label: "Failed", tone: "text-red-700 bg-red-50", Icon: XCircle },
  needs_review: { label: "Under review", tone: "text-red-700 bg-red-50", Icon: XCircle },
};

export const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : "";
};

export const startCheckout = (link) => {
  sessionStorage.setItem("dfa_paystack_pending", JSON.stringify({ reference: link.reference, invoice_ids: link.invoice_ids || [], combined: true }));
  window.location.assign(link.authorization_url);
};

const Shell = ({ title, icon, children, testId }) => <div className="mt-2 rounded-xl border border-[#1B2C5A]/10 bg-white p-4 shadow-sm" data-testid={testId}>
  {title && <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#1B2C5A]/70 font-medium mb-3">{icon}{title}</div>}
  {children}
</div>;

const Row = ({ label, value, strong }) => <div className="flex justify-between gap-3 py-1 text-sm">
  <span className="min-w-0 break-words text-[#475569]">{label}</span><span className={"shrink-0 whitespace-nowrap text-right " + (strong ? "font-semibold text-[#1B2C5A]" : "text-[#1B2C5A]")}>{value}</span>
</div>;

const StateBadge = ({ state }) => {
  const meta = STATE[state] || STATE.failed;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.tone}`}><meta.Icon className="w-3 h-3" />{meta.label}</span>;
};

const BalanceSummary = ({ block, onQuick, busy }) => {
  const allIds = block.students.flatMap((student) => student.periods.flatMap((period) => period.items.map((item) => item.invoice_id)));
  if (!block.students.length) return null;
  return <Shell title={block.scope?.type === "current_term" ? `Outstanding · ${block.scope.term_name || "This term"}` : "Outstanding bills"} testId="assistant-balance">
    {block.students.map((student) => <div key={student.student_id} className="mb-3 last:mb-0">
      <div className="font-display text-[#1B2C5A]">{student.name}</div>
      {student.periods.map((period) => <div key={`${period.session_name}-${period.term_name}`} className="mt-1 pl-3 border-l-2 border-[#9DCD3F]/50">
        <div className="text-xs text-[#475569]">{[period.term_name, period.session_name].filter(Boolean).join(" · ")}</div>
        {period.items.map((item) => <div key={item.invoice_id} className="flex justify-between gap-3 text-sm py-0.5">
          <span className="min-w-0 break-words">{item.title}{item.payment_in_progress && <span className="ml-2 text-[10px] uppercase text-amber-700">payment open</span>}</span>
          <span className="shrink-0 whitespace-nowrap font-medium">{money(item.outstanding_minor)}</span>
        </div>)}
      </div>)}
      {block.students.length > 1 && <Row label={`${student.name.split(" ")[0]} total`} value={money(student.total_minor)} />}
    </div>)}
    <div className="border-t mt-2 pt-2"><Row label="Total outstanding" value={money(block.total_outstanding_minor)} strong /></div>
    {block.available_credit_minor > 0 && <p className="text-xs text-[#475569] mt-1">Unallocated credit on account: {money(block.available_credit_minor)}. The bursary applies it to bills.</p>}
    <div className="flex flex-wrap gap-2 mt-3">
      <Button size="sm" disabled={busy} onClick={() => onQuick({ type: "prepare_invoice_payment", invoice_ids: allIds })} data-testid="assistant-pay-all"><CreditCard className="w-4 h-4 mr-1" />Pay {block.students.length > 1 || allIds.length > 1 ? "all" : "now"}</Button>
      {block.students.length > 1 && block.students.map((student) => <Button key={student.student_id} size="sm" variant="outline" disabled={busy}
        onClick={() => onQuick({ type: "prepare_invoice_payment", invoice_ids: student.periods.flatMap((period) => period.items.map((item) => item.invoice_id)) })}>
        Pay for {student.name.split(" ")[0]}</Button>)}
    </div>
  </Shell>;
};

const ChildOrder = ({ item, child, onQuick, busy }) => {
  const [quantity, setQuantity] = useState(1);
  const order = (extra = {}) => onQuick({ type: "prepare_order", product_id: item.product_id, student_id: child.student_id, quantity, ...extra });
  return <div className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm">
    <span className="text-[#1B2C5A]">{child.student_name}</span>
    {child.eligible ? <span className="flex items-center gap-2">
      {child.max_quantity > 1 && <select aria-label="Quantity" className="border rounded-md px-2 py-1 text-sm" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))}>
        {Array.from({ length: child.max_quantity }, (_, index) => index + 1).map((value) => <option key={value} value={value}>{value}</option>)}
      </select>}
      <Button size="sm" disabled={busy} onClick={() => order()}>Order</Button>
    </span> : child.reason === "choose_programme" ? <span className="flex flex-wrap gap-1">
      {(child.placements || []).map((placement) => <Button key={placement.enrollment_id} size="sm" variant="outline" disabled={busy} onClick={() => order({ enrollment_id: placement.enrollment_id })}>{placement.program_name}</Button>)}
    </span> : child.reason === "unpaid_order_exists" ? <Button size="sm" variant="outline" disabled={busy} onClick={() => onQuick({ type: "prepare_invoice_payment", invoice_ids: child.unpaid_invoice_ids })}>
      Pay unpaid order ({money(child.unpaid_amount_minor)})</Button> : <span className="text-xs text-[#475569]">{REASON_TEXT[child.reason] || "Not available"}</span>}
  </div>;
};

const ProductOptions = ({ block, onQuick, busy }) => <Shell title="Approved items" testId="assistant-products">
  {block.items.map((item) => <div key={item.product_id} className="mb-3 last:mb-0 border-b last:border-0 pb-2 last:pb-0">
    <div className="flex justify-between gap-3"><span className="font-display text-[#1B2C5A]">{item.name}</span><span className="font-semibold">{money(item.unit_price_minor)}<span className="text-xs font-normal text-[#475569]"> / {item.unit || "unit"}</span></span></div>
    {item.children.map((child) => <ChildOrder key={child.student_id} item={item} child={child} onQuick={onQuick} busy={busy} />)}
  </div>)}
</Shell>;

const PaymentConfirmation = ({ block, onConfirm, onCancel, busy, resolved }) => <Shell title={block.kind === "product_order" ? block.item_name : "Bill payment"} icon={<CreditCard className="w-3.5 h-3.5" />} testId="assistant-confirmation">
  {block.kind === "product_order" ? <>
    <Row label="Student" value={[block.student_name, block.class_name].filter(Boolean).join(" · ")} />
    <Row label="Term" value={block.term_name} />
    <Row label="Quantity" value={block.quantity} />
    {block.quantity > 1 && <Row label="Unit price" value={money(block.unit_price_minor)} />}
  </> : block.lines.map((line) => <Row key={line.invoice_id} label={`${line.title} · ${line.student_name.split(" ")[0]}`} value={money(line.amount_minor)} />)}
  <div className="border-t mt-2 pt-2"><Row label="Amount" value={money(block.total_minor)} strong /></div>
  {resolved ? <p className="text-xs text-[#475569] mt-3">{resolved}</p> : <>
    <p className="text-xs text-[#475569] mt-2">Nothing is charged until you press Generate Payment.</p>
    <div className="flex gap-2 mt-3 justify-end">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onCancel(block.action_id)}>Cancel</Button>
      <Button size="sm" disabled={busy} onClick={() => onConfirm(block.action_id)} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="assistant-generate-payment">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Generate Payment</Button>
    </div>
  </>}
</Shell>;

const PaymentLink = ({ block }) => {
  const expired = block.expires_at && new Date(block.expires_at) < new Date();
  return <Shell title={block.reused ? "Open payment" : "Payment request created"} icon={<CheckCircle2 className="w-3.5 h-3.5 text-[#9DCD3F]" />} testId="assistant-payment-link">
    <Row label="Amount" value={money(block.amount_minor)} strong />
    <Row label="Status" value={expired ? "Link expired" : "Pending"} />
    <Row label="Reference" value={<span className="text-xs font-mono break-all">{block.reference}</span>} />
    {expired ? <p className="text-xs text-[#475569] mt-2">This link has expired. Ask me again to create a fresh one.</p>
      : <Button className="w-full mt-3 bg-[#9DCD3F] hover:bg-[#8BBB2F] text-[#1B2C5A] font-semibold" onClick={() => startCheckout(block)} data-testid="assistant-pay-now">PAY NOW</Button>}
  </Shell>;
};

const PaymentStatus = ({ block, onQuick, busy }) => <Shell title="Payment status" testId="assistant-status">
  {block.checkouts.map((row) => <div key={row.reference} className="py-2 border-b last:border-0">
    <div className="flex justify-between gap-2 items-center"><span className="font-semibold text-[#1B2C5A]">{money(row.amount_minor)}</span><StateBadge state={row.state} /></div>
    <div className="text-xs text-[#475569]">{row.items.map((item) => [item.title, item.student_name?.split(" ")[0]].filter(Boolean).join(" · ")).join(", ")}</div>
    <div className="text-[11px] text-[#475569]/80 font-mono break-all">{row.reference} · {formatDate(row.verified_at || row.created_at)}</div>
    {row.state === "awaiting_payment" && <p className="text-xs text-amber-700 mt-1">Not yet confirmed by the payment gateway. If you have just paid, it can take a few minutes.</p>}
    {row.state === "confirmed" && <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => onQuick({ type: "receipt", reference: row.reference })}><ReceiptText className="w-4 h-4 mr-1" />View receipt</Button>}
  </div>)}
</Shell>;

const PaymentHistory = ({ block, onQuick, busy }) => <Shell title="Recent payments" testId="assistant-history">
  {block.payments.map((row) => <div key={row.payment_id} className="py-2 border-b last:border-0 flex justify-between gap-3 items-start">
    <div className="min-w-0">
      <div className="text-sm text-[#1B2C5A]">{row.student_name} · {formatDate(row.received_at)}</div>
      <div className="text-xs text-[#475569]">{row.items.map((item) => item.title).join(", ") || "Payment on account"}</div>
    </div>
    <div className="text-right shrink-0"><div className="font-semibold">{money(row.amount_minor)}</div>
      <button className="text-xs text-[#1B2C5A] underline" disabled={busy} onClick={() => onQuick({ type: "receipt", payment_id: row.payment_id })}>Receipt</button></div>
  </div>)}
</Shell>;

const Receipt = ({ block }) => {
  const [downloading, setDownloading] = useState("");
  const download = async (row) => {
    setDownloading(row.payment_id);
    try {
      const { data } = await api.get(row.pdf_path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a"); link.href = url; link.download = `DFA-receipt-${row.reference}.pdf`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) { toast.error(formatApiError(error)); } finally { setDownloading(""); }
  };
  return block.receipts.map((row) => <Shell key={row.payment_id} title="Official receipt" icon={<ReceiptText className="w-3.5 h-3.5" />} testId="assistant-receipt">
    <Row label="Student" value={row.student_name} />
    <Row label="Date" value={formatDate(row.received_at)} />
    {row.items.map((item, index) => <Row key={index} label={item.title} value={money(item.amount_minor)} />)}
    <div className="border-t mt-2 pt-2"><Row label="Amount paid" value={money(row.amount_minor)} strong /></div>
    <div className="text-[11px] text-[#475569]/80 font-mono break-all mt-1">{row.reference}</div>
    <Button size="sm" variant="outline" className="mt-3" disabled={downloading === row.payment_id} onClick={() => download(row)}>
      {downloading === row.payment_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download PDF</Button>
  </Shell>);
};

const StudentSelector = ({ block, onSend, busy }) => <Shell testId="assistant-student-selector">
  <div className="text-sm text-[#1B2C5A] mb-2">{block.question}</div>
  <div className="flex flex-wrap gap-2">{block.children.map((child) => <Button key={child.student_id} size="sm" variant="outline" disabled={busy} onClick={() => onSend(`It's for ${child.name}.`)}>{child.name}</Button>)}</div>
</Shell>;

const Capabilities = ({ block }) => <Shell title="What I can help with" testId="assistant-capabilities">
  {block.available.length
    ? <ul className="list-disc pl-5 space-y-1 text-sm text-[#1B2C5A]">{block.available.map((line) => <li key={line}>{line}</li>)}</ul>
    : <p className="text-sm text-[#475569]">I can't look up school records for your account yet.</p>}
  {block.not_yet_available.length > 0 && <>
    <div className="mt-3 mb-1 text-xs text-[#475569]">Not yet available in the assistant</div>
    <ul className="list-disc pl-5 space-y-0.5 text-xs text-[#475569]">{block.not_yet_available.map((line) => <li key={line}>{line}</li>)}</ul>
  </>}
</Shell>;

export default function AssistantBlock({ block, onQuick, onSend, onConfirm, onCancel, busy, resolvedActions = {} }) {
  switch (block.type) {
    case "balance_summary": return <BalanceSummary block={block} onQuick={onQuick} busy={busy} />;
    case "product_options": return <ProductOptions block={block} onQuick={onQuick} busy={busy} />;
    case "payment_confirmation": return <PaymentConfirmation block={block} onConfirm={onConfirm} onCancel={onCancel} busy={busy} resolved={resolvedActions[block.action_id]} />;
    case "payment_link": return <PaymentLink block={block} />;
    case "payment_status": return block.checkouts?.length ? <PaymentStatus block={block} onQuick={onQuick} busy={busy} /> : null;
    case "payment_history": return block.payments?.length ? <PaymentHistory block={block} onQuick={onQuick} busy={busy} /> : null;
    case "receipt": return <Receipt block={block} />;
    case "student_selector": return <StudentSelector block={block} onSend={onSend} busy={busy} />;
    case "capabilities": return <Capabilities block={block} />;
    case "notice": return <p className="mt-2 text-xs text-[#475569]">{block.text}</p>;
    default: return null;
  }
}
